import { useState } from 'react';
import { usePremiumPageCopy } from './copy';
import { useTranslation } from 'react-i18next';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { ParticipantProgress } from '../BoxPage/ParticipantProgress';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { DateRangePicker } from '@src/components/DateRangeCalendar/DateRangePicker';
import { HistoryParticipantFilter } from '../BoxPage/HistoryParticipantFilter';
import { ActivityEventIcon } from '../BoxPage/ActivityEventIcon';
import { presentActivity, type Activity } from '../BoxPage/activityPresentation';

function exampleTime(daysAgo: number, hours: number, minutes: number) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

/** Live cards, filters and history presentation with local-only demonstration events. */
export function HistoryExample() {
  const copy = usePremiumPageCopy();
  const { t, i18n } = useTranslation();
  const [participant, setParticipant] = useState('');
  const [dates, setDates] = useState({ start: '', end: '' });
  const events: Activity[] = [
    { id: 1, type: 'participant.joined', cardId: 1, participantName: copy.olivia, participantPicture: 'rabbit', createdAt: exampleTime(0, 14, 30), metadata: {} },
    { id: 2, type: 'box.settings_updated', actorType: 'organizer', actorCardId: 2, participantName: null, createdAt: exampleTime(0, 12, 15), metadata: { actor: { name: copy.alex, pictureId: null, avatar: null }, before: { useWish: false }, after: { useWish: true } } },
    { id: 3, type: 'message.sent', cardId: 3, participantName: copy.michael, participantPicture: 'bear', createdAt: exampleTime(1, 18, 0), metadata: { direction: 'santa', senderName: copy.michael } },
    { id: 4, type: 'gift.sent', cardId: 1, participantName: copy.olivia, participantPicture: 'rabbit', createdAt: exampleTime(2, 11, 0), metadata: {} },
    { id: 5, type: 'gift.received', cardId: 3, participantName: copy.michael, participantPicture: 'bear', createdAt: exampleTime(6, 16, 30), metadata: {} },
  ];
  // Only identity fields are consumed by this shared filter; no real card is loaded.
  const people = [{ id: 1, username: copy.olivia }, { id: 3, username: copy.michael }] as CardInfo[];
  const filtered = events.filter(event => {
    if (participant === 'management' ? Boolean(event.cardId) : participant && String(event.cardId) !== participant) return false;
    const date = new Date(event.createdAt);
    const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return (!dates.start || day >= dates.start) && (!dates.end || day <= dates.end);
  });
  return <div className="box-activity premium-progress-example" aria-label={copy.exampleGameHistory} data-testid="premium-history-example">
    <div>
      <ParticipantProgress readOnly progress={{ total: 12, engagement: { notOpened: 0, opened: 12, filled: 8 }, claimed: 12, revealed: 9, sent: 6, received: 4, activeDraw: true }} cards={[]} box={{ key: 'premium-example' } as BoxInfo['box']}>
        <details open className="participant-progress__history">
          <summary className="box-activity__history-heading"><h2>{t('participantProgress.history')}</h2><ChevronDownIcon /></summary>
          <div className="participant-progress__filters"><div className="history-controls"><div className="history-filters">
            <HistoryParticipantFilter cards={people} value={participant} onChange={setParticipant} />
            <DateRangePicker value={dates} onChange={setDates} />
          </div></div></div>
          {!filtered.length && <p>{t('boxActivity.empty')}</p>}
          <ol className="box-activity__list">{filtered.map(event => {
            const presentation = presentActivity(event, t, (_field, value) => String(value ?? '—'), name => <span className="box-activity__person"><span className="box-activity__pairing-avatar" aria-hidden="true"><ParticipantFace picture={event.participantPicture || 'fox'} /></span><b>{name}</b></span>, 2);
            return <li key={event.id}><span className="box-activity__avatar" aria-hidden="true"><ActivityEventIcon type={event.type} /></span><div><strong className="box-activity__title-regular">{presentation.title}</strong><time dateTime={event.createdAt}>{new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(event.createdAt))}</time></div></li>;
          })}</ol>
        </details>
      </ParticipantProgress>
    </div>
  </div>;
}
